import React, { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import {
  X,
  Globe,
  Loader2,
  Rocket,
  Eye,
  ChevronRight,
  MapPin,
  Languages,
  Link2,
  FileText,
} from "lucide-react";
import {
  NICHE_OPTIONS,
  GEO_OPTIONS,
  DOMAIN_OPTIONS,
  GEO_LANGUAGE_MAP,
} from "@ymonetize/shared";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (taskId: string) => void;
}

export function CreateFunnelModal({ open, onClose, onSuccess }: Props) {
  const [mode, setMode] = useState<"fast" | "inspiration">("fast");
  const [niche, setNiche] = useState("");
  const [customNiche, setCustomNiche] = useState("");
  const [geo, setGeo] = useState("US");
  const [language, setLanguage] = useState("en");
  const [subdomain, setSubdomain] = useState("");
  const [baseDomain, setBaseDomain] = useState<string>(DOMAIN_OPTIONS[0].value);
  const [inspirationUrl, setInspirationUrl] = useState("");
  const [notes, setNotes] = useState("");

  const createFunnel = trpc.brain.createFunnel.useMutation({
    onSuccess: (task) => {
      onSuccess(task.id);
      onClose();
    },
  });

  function handleGeoChange(newGeo: string) {
    setGeo(newGeo);
    setLanguage(GEO_LANGUAGE_MAP[newGeo] || "en");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const finalNiche = niche === "custom" ? customNiche : niche;
    if (!finalNiche) return;

    createFunnel.mutate({
      mode,
      niche: finalNiche,
      geo,
      language,
      subdomain: subdomain || undefined,
      baseDomain,
      inspirationUrl: mode === "inspiration" ? inspirationUrl || undefined : undefined,
      notes: notes || undefined,
    });
  }

  if (!open) return null;

  const isValid = (niche === "custom" ? customNiche.length > 0 : niche.length > 0) &&
    (mode === "fast" || (mode === "inspiration" && inspirationUrl.length > 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-card border border-border rounded-2xl shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Criar Funil</h2>
              <p className="text-xs text-muted-foreground">Deploy rápido de site de nicho</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Mode Selector */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Modo</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("fast")}
                className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-all ${
                  mode === "fast"
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:border-primary/30"
                }`}
              >
                <Rocket className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium">Fast Deploy</p>
                  <p className="text-[10px] text-muted-foreground">Template parametrizado</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode("inspiration")}
                className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-all ${
                  mode === "inspiration"
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:border-primary/30"
                }`}
              >
                <Eye className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium">Inspiração</p>
                  <p className="text-[10px] text-muted-foreground">Baseado em URL</p>
                </div>
              </button>
            </div>
          </div>

          {/* Niche */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <ChevronRight className="w-3.5 h-3.5 text-primary" />
              Nicho
            </label>
            <select
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
            >
              <option value="">Selecione um nicho...</option>
              {NICHE_OPTIONS.map((n) => (
                <option key={n.value} value={n.value}>{n.label}</option>
              ))}
            </select>
            {niche === "custom" && (
              <input
                type="text"
                value={customNiche}
                onChange={(e) => setCustomNiche(e.target.value)}
                placeholder="Descreva o nicho personalizado..."
                className="w-full mt-2 px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              />
            )}
          </div>

          {/* Geo + Language */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-primary" />
                País
              </label>
              <select
                value={geo}
                onChange={(e) => handleGeoChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              >
                {GEO_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                <Languages className="w-3.5 h-3.5 text-primary" />
                Idioma
              </label>
              <input
                type="text"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              />
            </div>
          </div>

          {/* Domain */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5 text-primary" />
              Domínio
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="subdomínio"
                className="flex-1 px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              />
              <span className="self-center text-muted-foreground text-sm">.</span>
              <select
                value={baseDomain}
                onChange={(e) => setBaseDomain(e.target.value)}
                className="px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              >
                {DOMAIN_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            {subdomain && (
              <p className="text-[10px] text-muted-foreground mt-1">
                URL final: {subdomain}.{baseDomain}
              </p>
            )}
          </div>

          {/* Inspiration URL (only in inspiration mode) */}
          {mode === "inspiration" && (
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-primary" />
                URL de Inspiração
              </label>
              <input
                type="url"
                value={inspirationUrl}
                onChange={(e) => setInspirationUrl(e.target.value)}
                placeholder="https://exemplo.com"
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-primary" />
              Observações
              <span className="text-[10px] text-muted-foreground">(opcional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instruções adicionais para o Brain..."
              rows={2}
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          </div>

          {/* Error */}
          {createFunnel.error && (
            <p className="text-xs text-destructive">{createFunnel.error.message}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!isValid || createFunnel.isPending}
            className="w-full py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
          >
            {createFunnel.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Criando funil...
              </>
            ) : (
              <>
                <Rocket className="w-4 h-4" />
                Criar Funil
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
